import React, { useState, useRef, useCallback } from 'react';
import styled from 'astroturf/react';
import { Modal } from 'js/design_system/Modal';
import { Icon } from 'js/design_system/Icon';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import trackClick from 'js/utils/trackClick';
import routes from 'js/core_pre_webpack/routes';
import { downloadFile } from 'js/citeright/utils/exportCitations';
import type { PublishedLetterMetadata, Work } from './CitationButton';

function useCopiedIndex(resetDelay = 2000) {
  const [copiedIndex, setIndex] = useState<number | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const markAsCopied = useCallback(
    (index: number) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      setIndex(index);

      timeoutRef.current = setTimeout(() => {
        setIndex(null);
        timeoutRef.current = null;
      }, resetDelay);
    },
    [resetDelay],
  );

  return [copiedIndex, markAsCopied] as const;
}

const StyledIcon = styled(Icon)`
  cursor: pointer;
  &:hover,
  &:focus {
    color: var(--primary-brand, #0645b1);
  }
`;

type CitationModalProps = {
  open: boolean;
  citations: { name: string; citation: string }[];
  onClose: () => void;
  entity_id: string | number; // used for impression tracking
  source: string; // used for impression tracking
  work?: Work;
  publishedLetterMetadata?: PublishedLetterMetadata;
  modalTitle?: string;
};

const CitationStyle = styled.div`
  @import 'stylesheets/design_system/font';
  @import 'stylesheets/design_system/breakpoint';
  @include body__medium;

  display: flex;
  gap: 16px;
  align-items: center;
  margin: 16px 0;

  @include breakpoint(small) {
    @include body__small;
    gap: 12px;
  }
`;

const CitationText = styled.div`
  @import 'stylesheets/design_system/font';
  @include body__extra-small;
  padding: 16px;
  background-color: var(--background-beige-light);
  max-width: 700px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: var(--background-beige-dark);
  }
`;

const CopiedNotification = styled.div`
  position: absolute;
  bottom: 8px;
  right: 8px;
  background-color: var(--brand-colors-success-500);
  color: var(--text-white);
  padding: 2px 8px;
  border-radius: var(--border-radius-radius-sm);
  font-size: var(--type-size-sm-1);
`;

type CitationItemProps = {
  name: string;
  citation: string;
  index: number;
  onCopy: (text: string, index: number) => Promise<void>;
  copiedIndex: number | null;
};

function CitationItem({
  name,
  citation,
  index,
  onCopy,
  copiedIndex,
}: CitationItemProps) {
  const isCopied = copiedIndex === index;

  return (
    <div>
      <CitationStyle>
        {name}
        <StyledIcon
          name={isCopied ? 'check' : 'content_copy'}
          size={14}
          onClick={() => onCopy(citation, index)}
        />
      </CitationStyle>
      <CitationText
        onClick={() => onCopy(citation, index)}
        style={{
          position: 'relative',
          backgroundColor: isCopied
            ? 'var(--brand-colors-success-100)'
            : undefined,
        }}
      >
        {citation}
        {isCopied && <CopiedNotification>Copied</CopiedNotification>}
      </CitationText>
    </div>
  );
}

const ExportLinksContainer = styled.div`
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--stroke-light, #e5e5e5);
  font-size: 14px;
  color: var(--text-secondary, #666);
`;

const ExportLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: var(--primary-brand, #0645b1);
  text-decoration: none;
  cursor: pointer;
  font-size: 14px;
  font-family: inherit;

  &:hover {
    text-decoration: underline;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  background-color: var(--error-light, #fff5f5);
  color: var(--error-dark, #b60000);
  border-radius: var(--border-radius-radius-sm);
  font-size: 14px;
`;

const CitationModal: React.FC<CitationModalProps> = ({
  open,
  citations,
  onClose,
  entity_id,
  source,
  work,
  publishedLetterMetadata,
  modalTitle = 'Cite this paper',
}) => {
  const [copiedIndex, markAsCopied] = useCopiedIndex();
  const [isExportingRis, setIsExportingRis] = useState(false);
  const [isExportingBibtex, setIsExportingBibtex] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const copyTextToClipboard = async (text: string, index: number) => {
    await navigator.clipboard.writeText(text);
    markAsCopied(index);
    if (source === 'letters_citation_modal') {
      trackClick({
        source: 'journal_citation_copy',
        entityId: entity_id,
        extra: { format: citations[index].name, work_id: work?.id },
      });
    }
  };

  const generateFilename = (extension: 'ris' | 'bib'): string => {
    if (!publishedLetterMetadata) return `citation.${extension}`;

    const parts: string[] = [];

    // Add sanitized journal name
    if (publishedLetterMetadata.journalName) {
      const sanitizedJournal = publishedLetterMetadata.journalName
        .replace(/[^a-zA-Z0-9\s-]/g, '')
        .replace(/\s+/g, '_');
      parts.push(sanitizedJournal);
    }

    // Add year
    if (publishedLetterMetadata.publicationYear) {
      parts.push(publishedLetterMetadata.publicationYear.toString());
    }

    // Add volume
    if (publishedLetterMetadata.volume) {
      parts.push(`v${publishedLetterMetadata.volume}`);
    }

    // Add issue
    if (publishedLetterMetadata.issue) {
      parts.push(`i${publishedLetterMetadata.issue}`);
    }

    const firstAuthor = publishedLetterMetadata.authors?.[0];
    const authorName =
      firstAuthor?.family ||
      firstAuthor?.literal ||
      firstAuthor?.name.split(' ').pop();
    const sanitizedAuthorName = authorName?.replace(/[^a-zA-Z0-9]/g, '');
    if (sanitizedAuthorName) parts.push(sanitizedAuthorName);

    return parts.length > 0
      ? `${parts.join('_')}.${extension}`
      : `citation.${extension}`;
  };

  const handleExport = async (format: 'ris' | 'bibtex') => {
    if (!work || !publishedLetterMetadata) return;

    const isRis = format === 'ris';
    const setLoading = isRis ? setIsExportingRis : setIsExportingBibtex;
    const extension = isRis ? 'ris' : 'bib';
    const formatName = isRis ? 'RIS' : 'BibTeX';

    setLoading(true);
    setExportError(null);

    try {
      const route = routes.journals_article_citation_download_path({
        doi: publishedLetterMetadata.doi,
        citation_format: format,
      });
      const response = await fetchWithCsrf(route);

      if (!response.ok) {
        throw new Error(`Failed to export ${formatName}`);
      }

      const blob = await response.blob();
      const filename = generateFilename(extension);

      // Don't pass contentDisposition - use our custom filename
      downloadFile(blob, filename);
      trackClick({
        source: 'journal_citation_export',
        entityId: entity_id,
        extra: { format, work_id: work.id },
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : `Failed to export ${formatName}`;
      setExportError(errorMessage);
      console.error(`Failed to export ${formatName}:`, error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportRis = () => handleExport('ris');
  const handleExportBibtex = () => handleExport('bibtex');

  return (
    <Modal title={modalTitle} open={open} onClose={onClose}>
      <ImpressionWatcher source={source} entity_type={15} entity_id={entity_id}>
        {work && publishedLetterMetadata && (
          <>
            <ExportLinksContainer>
              Export citation file:{' '}
              <ExportLink
                onClick={handleExportBibtex}
                disabled={isExportingBibtex}
              >
                {isExportingBibtex ? 'Downloading...' : 'BibTeX'}
              </ExportLink>
              {' | '}
              <ExportLink onClick={handleExportRis} disabled={isExportingRis}>
                {isExportingRis ? 'Downloading...' : 'RIS'}
              </ExportLink>{' '}
              (EndNote, Mendeley, Zotero)
            </ExportLinksContainer>
            {exportError && <ErrorMessage>{exportError}</ErrorMessage>}
          </>
        )}
        {citations.map(({ name, citation }, index) => (
          <CitationItem
            key={name}
            name={name}
            citation={citation}
            index={index}
            onCopy={copyTextToClipboard}
            copiedIndex={copiedIndex}
          />
        ))}
      </ImpressionWatcher>
    </Modal>
  );
};

export default CitationModal;
